/**
 * ab_utils — A/B-Test-Varianten-Zuweisung.
 *
 * Weist neuen Usern deterministisch eine Variante zu und persistiert sie
 * in localStorage, damit der gleiche User immer die gleiche Variante sieht.
 *
 * Verwendung:
 *   import { getVariant } from "@/lib/ab_utils";
 *   const variant = getVariant("AB-01");          // "A" oder "B"
 *   const variant = getVariant("AB-01", { trackFn: trackEvent });
 */

const STORAGE_KEY = "ff_ab_variants";

function loadVariants() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveVariant(experimentId, variant) {
  try {
    const variants = loadVariants();
    variants[experimentId] = variant;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(variants));
  } catch {
    /* localStorage nicht verfügbar — ignorieren */
  }
}

/**
 * Gibt die zugewiesene Variante für ein Experiment zurück.
 * Beim ersten Aufruf wird zufällig eine Variante gezogen, persistiert
 * und (falls trackFn übergeben) als Event geloggt.
 *
 * @param {string} experimentId  — z.B. "AB-01"
 * @param {{ variants?: string[], trackFn?: Function }} opts
 * @returns {string}             — "A" | "B" (oder erster Eintrag aus variants)
 */
export function getVariant(experimentId, { variants = ["A", "B"], trackFn } = {}) {
  const stored = loadVariants();
  if (stored[experimentId]) return stored[experimentId];

  const variant = variants[Math.floor(Math.random() * variants.length)];
  saveVariant(experimentId, variant);

  if (typeof trackFn === "function") {
    trackFn("ab_variant_assigned", { experiment: experimentId, variant });
  }

  return variant;
}

/** Gibt alle aktuell gespeicherten Varianten zurück (für Debugging). */
export function getAllVariants() {
  return loadVariants();
}

/** Setzt eine Variante manuell (z.B. für QA/Vorschau). */
export function forceVariant(experimentId, variant) {
  saveVariant(experimentId, variant);
}

/** Löscht alle Varianten (z.B. nach Logout). */
export function clearVariants() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
